import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { AuthErrorType, AuthError, LoginCredentials } from '../types/auth';
import LoadingIndicator from '../components/LoadingIndicator';
import { apiService } from '../services/apiService';
import { Dialog } from '@headlessui/react'
import { SchoolLevel, Grade } from '../services/adminService';

const Login: React.FC = () => {
    const login = useAuthStore((state) => state.login);
    const error = useAuthStore((state) => state.error);
    const loading = useAuthStore((state) => state.loading.login);
    const clearError = useAuthStore((state) => state.clearError);
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    
    const [isEmail, setIsEmail] = useState(true);
    const [isLoginMode, setIsLoginMode] = useState(true);
    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [name, setName] = useState('');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [successMessage, _setSuccessMessage] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const [schoolLevel, setSchoolLevel] = useState<SchoolLevel | undefined>(undefined);
    const [grade, setGrade] = useState<Grade | undefined>(undefined);
    
    const [availableGrades, setAvailableGrades] = useState<string[]>([]);

    useEffect(() => {
        if (isAuthenticated) {
          const { user } = useAuthStore.getState();
          const redirectPath = user?.role === 'ADMIN' ? '/admin' : '/chat';
          const from = (location.state as any)?.from?.pathname || redirectPath;
          navigate(from, { replace: true });
        }
      }, [isAuthenticated, navigate, location]);
    
      useEffect(() => {
        if (error) {
          console.log('Current error state:', {
            type: error.type,
            message: error.message
          });
        }
      }, [error]);
    
      useEffect(() => {
        const primaryGrades = ['0', '1', '2', '3', '4', '5', '6', '7'];
        const secondaryGrades = ['form1', 'form2', 'form3', 'form4', 'form5', 'form6'];
        
        if (schoolLevel === 'primary') {
          setAvailableGrades(primaryGrades);
        } else if (schoolLevel === 'secondary') {
          setAvailableGrades(secondaryGrades);
        } else {
          setAvailableGrades([]);
        }
        setGrade(undefined);
      }, [schoolLevel]);
    const getErrorMessage = (error: AuthError) => {
        console.log('Getting error message for:', error);
            
            switch (error.type) {
            case AuthErrorType.INVALID_CREDENTIALS:
                return 'Invalid email/phone or password';
            case AuthErrorType.NETWORK_ERROR:
                return 'Unable to connect to server. Please check your internet connection.';
            case AuthErrorType.SESSION_EXPIRED:
                return 'Your session has expired. Please login again.';
            case AuthErrorType.AUTHENTICATION:
                return error.message || 'Authentication failed';
            case AuthErrorType.UNKNOWN:
                return error.message || 'An unexpected error occurred';
            default:
                return error.message || 'An error occurred';
            }
        };

    const renderError = () => {
        if (!error) return null;
        
        return (
        <div className="p-4 mb-6 border border-red-400/30 bg-red-400/10 rounded-xl backdrop-blur-sm">
            <div className="text-red-300 text-sm font-medium">
            {getErrorMessage(error)}
            </div>
        </div>
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        clearError();

        if (!isLoginMode && password !== confirmPassword) {
        return;
        }

        try {
        if (isLoginMode) {
            const credentials: LoginCredentials = {
            identifier,
            password
            };
            
            const result = await login(credentials);
            if (result) {
              // Login successful, navigation will be handled by useEffect
              console.log('Login successful:', result);
            }
        } else {
            // Sign up flow
            const signupData = {
            email: isEmail ? identifier : undefined,
            whatsapp: !isEmail ? identifier : undefined,
            first_name: name.split(' ')[0],
            last_name: name.split(' ').slice(1).join(' ') || '',
            password,
            user_type: 'USER' as const,
            school_level: schoolLevel,
            grade: grade
            };

            await apiService.request({
            url: '/auth/signup',
            method: 'POST',
            data: signupData
            });

            // Set modal message based on signup method
            const verificationMessage = isEmail
            ? "Please check your email for verification instructions. Don't forget to check your spam folder!"
            : "Please check your WhatsApp messages for verification instructions. We'll send you a message shortly!";

            // Show modal with verification message
            setModalMessage(verificationMessage);
            setIsModalOpen(true);
            
            // Switch back to login mode
            setIsLoginMode(true);
            
            // Clear form fields
            setIdentifier('');
            setPassword('');
            setConfirmPassword('');
            setName('');
        }
        } catch (error) {
        console.error('Error:', error);
        }
    };


    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0f172a] to-[#1e293b] p-4">
        <div className="w-full max-w-md space-y-8">
            {/* Header */}
            <div className="text-center">
            <h1 className="text-4xl font-extrabold mb-2 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                AI Education Assistant
            </h1>
            {/* <h2 className="text-xl text-slate-300">
                {isLoginMode ? 'Welcome Back' : 'Start Your Journey'}
            </h2> */}
            </div>

            {/* Mode Toggle and Home Link */}
            <div className="flex flex-col items-center space-y-3">
                <button
                    onClick={() => setIsLoginMode(!isLoginMode)}
                    className="text-slate-400 hover:text-cyan-400 transition-colors text-sm font-medium"
                >
                    {isLoginMode 
                    ? 'Need an account? Sign up →'
                    : 'Already have an account? Sign in →'}
                </button>
                
                <div className="text-slate-500 text-xs">or</div>
                
                {/* Back to Home Button */}
                <button
                    onClick={() => navigate('/')}
                    className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors text-sm font-medium group"
                >
                    <svg className="w-4 h-4 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Home
                </button>
            </div>

            {/* Error/Success Messages */}
            {renderError()}
            {successMessage && (
            <div className="p-4 mb-6 border border-green-400/30 bg-green-400/10 rounded-xl backdrop-blur-sm">
                <div className="text-green-300 text-sm font-medium">
                {successMessage}
                </div>
            </div>
            )}

            {/* Form Container */}
            <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 transition-all duration-300 hover:border-cyan-400/30">
            <form className="space-y-6" onSubmit={handleSubmit}>
                {/* Form Fields */}
                <div className="space-y-5">
                {!isLoginMode && (
                    <div>
                    <input
                        id="name"
                        required
                        className="w-full px-4 py-3 bg-slate-800/20 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 
                                focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-all"
                        placeholder="Full Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    </div>
                )}

                {/* Email/WhatsApp Toggle */}
                <div className="flex gap-2">
                    <button
                    type="button"
                    onClick={() => setIsEmail(true)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                        isEmail 
                        ? 'bg-cyan-400/10 border border-cyan-400/20 text-cyan-400'
                        : 'border border-slate-700/50 hover:border-slate-600 text-slate-400'
                    }`}
                    >
                    Email
                    </button>
                    <button
                    type="button"
                    onClick={() => setIsEmail(false)}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                        !isEmail 
                        ? 'bg-cyan-400/10 border border-cyan-400/20 text-cyan-400'
                        : 'border border-slate-700/50 hover:border-slate-600 text-slate-400'
                    }`}
                    >
                    WhatsApp
                    </button>
                </div>

                {/* Identifier Input */}
                <div>
                    <input
                    id="identifier"
                    type={isEmail ? 'email' : 'tel'}
                    required
                    className="w-full px-4 py-3 bg-slate-800/20 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 
                            focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-all"
                    placeholder={isEmail ? 'Email address' : 'WhatsApp number'}
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    />
                </div>

                {/* Password Inputs */}
                <div>
                    <input
                    id="password"
                    type="password"
                    required
                    className="w-full px-4 py-3 bg-slate-800/20 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 
                            focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-all"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    />
                </div>

                {!isLoginMode && (
                    <div>
                    <input
                        id="confirmPassword"
                        type="password"
                        required
                        className="w-full px-4 py-3 bg-slate-800/20 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 
                                focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-all"
                        placeholder="Confirm Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    </div>
                )}

                {/* School Level/Grade Selectors */}
                {!isLoginMode && (
                    <>
                    <select
                        value={schoolLevel || ''}
                        onChange={(e) => setSchoolLevel(e.target.value as SchoolLevel || undefined)}
                        className="w-full px-4 py-3 bg-slate-800/20 border border-slate-700/50 rounded-lg text-slate-200 
                                focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-all"
                    >
                        <option value="" className="bg-slate-800">Select School Level</option>
                        <option value="primary" className="bg-slate-800">Primary</option>
                        <option value="secondary" className="bg-slate-800">Secondary</option>
                    </select>

                    {schoolLevel && (
                        <select
                        value={grade || ''}
                        onChange={(e) => setGrade(e.target.value as Grade || undefined)}
                        className="w-full px-4 py-3 bg-slate-800/20 border border-slate-700/50 rounded-lg text-slate-200 
                                focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400 transition-all"
                        >
                        <option value="" className="bg-slate-800">Select Grade</option>
                        {availableGrades.map(grade => (
                            <option key={grade} value={grade} className="bg-slate-800">{grade}</option>
                        ))}
                        </select>
                    )}
                    </>
                )}
                </div>

                {/* Submit Button */}
                <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
                    loading 
                    ? 'bg-cyan-400/50 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:scale-[1.02] shadow-lg shadow-cyan-500/20'
                }`}
                >
                {loading ? (
                    <div className="flex items-center justify-center gap-2">
                    <LoadingIndicator size="small" color="text-white" />
                    <span>Signing {isLoginMode ? 'in' : 'up'}...</span>
                    </div>
                ) : (
                    isLoginMode ? 'Sign In' : 'Create Account'
                )}
                </button>
            </form>
            </div>
        </div>

        {/* Verification Modal */}
        <Dialog
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            className="relative z-50"
        >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 max-w-md w-full">
                <Dialog.Title className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent mb-4">
                Verification Required
                </Dialog.Title>
                <div className="text-slate-300 mb-6">
                {modalMessage}
                </div>
                <button
                onClick={() => setIsModalOpen(false)}
                className="w-full py-2 px-4 bg-cyan-400/10 border border-cyan-400/20 text-cyan-400 rounded-lg hover:bg-cyan-400/20 transition-colors"
                >
                Got it, thanks!
                </button>
            </Dialog.Panel>
            </div>
        </Dialog>
        </div>
    );
    };

    export default Login;